import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { MsalProvider } from '@azure/msal-react';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'dayjs/locale/pl';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import { routeTree } from './routeTree.gen';
import { msalInstance } from './auth/msalConfig';

dayjs.locale('pl');
// Udostępnia jednostkę 'quarter' w `startOf`/`endOf` — używa jej preset kwartału
// w filtrach okresu (`raporty/projekty/OkresPicker.tsx`).
dayjs.extend(quarterOfYear);

const router = createRouter({ routeTree });
const queryClient = new QueryClient();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function bootstrap() {
  await msalInstance.initialize();

  // Consume the redirect response before the router mounts — otherwise the
  // index-route redirect navigates away and strips the MSAL response hash.
  const result = await msalInstance.handleRedirectPromise();
  const account = result?.account ?? msalInstance.getAllAccounts()[0];
  if (account) {
    msalInstance.setActiveAccount(account);
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider>
            <DatesProvider settings={{ locale: 'pl', firstDayOfWeek: 1 }}>
              <RouterProvider router={router} />
            </DatesProvider>
          </MantineProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

void bootstrap();
